import React from 'react';

import { Box, Text, useTheme } from 'app/shopify/theme';

export type TagType = 'primary' | 'primaryNeutral' | 'secondary';
export type TagSize = 'default' | 'small';

export interface TagProps {
  label: string;
  type?: TagType;
  size?: TagSize;
  inverted?: boolean;
  leadingIcon?: React.ReactNode;
}

const LABEL_VARIANT_BY_SIZE: Record<TagSize, 'bodyXSmall' | 'bodyCaption'> = {
  default: 'bodyXSmall',
  small: 'bodyCaption',
};

const LABEL_PADDING_HORIZONTAL_BY_SIZE: Record<TagSize, 'spacing.general.x1' | 'spacing.general.x0,5'> = {
  default: 'spacing.general.x1',
  small: 'spacing.general.x0,5',
};

const ROOT_PADDING_HORIZONTAL_BY_SIZE: Record<TagSize, 'spacing.general.x1'> = {
  default: 'spacing.general.x1',
  small: 'spacing.general.x1',
};

const ROOT_PADDING_VERTICAL_BY_SIZE: Record<TagSize, 'spacing.general.x1' | 'spacing.general.x0,5'> = {
  default: 'spacing.general.x1',
  small: 'spacing.general.x0,5',
};

const ICON_SIZE_BY_SIZE: Record<TagSize, number> = {
  default: 16,
  small: 12,
};

function getTagColorKeys(type: TagType, inverted: boolean) {
  if (type === 'primary') {
    return {
      background: inverted ? 'surface.default' : 'surface.brand.strong',
      text: inverted ? 'text.brand' : 'text.inverse.primary',
    } as const;
  }

  if (type === 'primaryNeutral') {
    return { background: 'surface.secondary-neutral', text: 'text.secondary' } as const;
  }

  return {
    background: undefined,
    text: inverted ? 'text.inverse.primary' : 'text.secondary',
  } as const;
}

export function Tag({ label, type = 'primary', size = 'default', inverted = true, leadingIcon }: TagProps) {
  const theme = useTheme();

  const hasBackground = type === 'primary' || type === 'primaryNeutral';
  const hasBorder = type === 'secondary';
  const colorKeys = getTagColorKeys(type, inverted);
  const backgroundColor = colorKeys.background ? theme.colors[colorKeys.background] : 'transparent';
  const borderColor = theme.colors[inverted ? 'border.inverse.primary' : 'border.secondary'];
  const textColor = theme.colors[colorKeys.text];

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      borderRadius="radius.full"
      paddingHorizontal={ROOT_PADDING_HORIZONTAL_BY_SIZE[size]}
      paddingVertical={ROOT_PADDING_VERTICAL_BY_SIZE[size]}
      borderWidth={hasBorder ? 1 : 0}
      style={{
        backgroundColor: hasBackground ? backgroundColor : 'transparent',
        borderColor: hasBorder ? borderColor : 'transparent',
      }}
    >
      {leadingIcon ? (
        <Box
          width={ICON_SIZE_BY_SIZE[size]}
          height={ICON_SIZE_BY_SIZE[size]}
          alignItems="center"
          justifyContent="center"
        >
          {leadingIcon}
        </Box>
      ) : null}

      <Box
        alignItems="center"
        justifyContent="center"
        paddingHorizontal={LABEL_PADDING_HORIZONTAL_BY_SIZE[size]}
        paddingTop="spacing.general.0"
        paddingBottom="spacing.general.x0,5"
      >
        <Text variant={LABEL_VARIANT_BY_SIZE[size]} style={{ color: textColor }}>
          {label}
        </Text>
      </Box>
    </Box>
  );
}
